
import React, { useState } from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { Rocket, Coins, Lock, Flame, CheckCircle, AlertCircle, ArrowRight, Layers } from 'lucide-react';

interface KolFundraisingCardProps {
  language: Language;
}

// Configuration for the 5 Rounds
const ROUNDS_CONFIG = [
    { id: 1, fdv: 10_000_000, price: 0.010, totalTokens: 20_000_000, raisedTokens: 18_500_000 },
    { id: 2, fdv: 12_000_000, price: 0.012, totalTokens: 20_000_000, raisedTokens: 2_000_000 },
    { id: 3, fdv: 15_000_000, price: 0.015, totalTokens: 20_000_000, raisedTokens: 0 },
    { id: 4, fdv: 18_000_000, price: 0.018, totalTokens: 20_000_000, raisedTokens: 0 },
    { id: 5, fdv: 20_000_000, price: 0.020, totalTokens: 20_000_000, raisedTokens: 0 },
];

const MAX_TOKENS_PER_KOL = 500_000;

export const KolFundraisingCard: React.FC<KolFundraisingCardProps> = ({ language }) => {
  const t = TRANSLATIONS[language];
  const [selectedRoundId, setSelectedRoundId] = useState(1);
  const [investAmount, setInvestAmount] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currentRound = ROUNDS_CONFIG.find(r => r.id === selectedRoundId) || ROUNDS_CONFIG[0];
  const maxUsdtAllocation = MAX_TOKENS_PER_KOL * currentRound.price;
  
  // Calculate tokens based on USDT input
  const calculatedTokens = investAmount ? Math.floor(Number(investAmount) / currentRound.price) : 0;
  const isOverLimit = calculatedTokens > MAX_TOKENS_PER_KOL;

  const handleInvest = () => {
    if (!investAmount) return;
    if (isOverLimit) {
        alert(`Max limit per round is ${MAX_TOKENS_PER_KOL.toLocaleString()} tokens.`);
        return;
    }
    
    setIsLoading(true);
    setTimeout(() => {
        setIsLoading(false);
        setSuccess(true);
    }, 1500);
  };

  const handleReset = () => {
      setSuccess(false);
      setInvestAmount('');
  }

  const progress = (currentRound.raisedTokens / currentRound.totalTokens) * 100;

  if (success) {
      return (
        <div className="bg-gradient-to-br from-indigo-900 to-purple-900 border border-indigo-500/50 rounded-xl p-8 text-center animate-in zoom-in-95 relative">
            <button onClick={handleReset} className="absolute top-4 right-4 text-indigo-300 hover:text-white text-xs">Close</button>
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/50">
                <CheckCircle size={32} className="text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{t.investSuccess}</h2>
            <p className="text-indigo-200">{t.investDesc}</p>
            <div className="mt-6 flex justify-center gap-4">
                <div className="p-4 bg-black/20 rounded-lg inline-block">
                    <p className="text-xs text-indigo-300 uppercase tracking-wider mb-1">USDT Invested</p>
                    <p className="text-xl font-bold text-white">${Number(investAmount).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-black/20 rounded-lg inline-block">
                    <p className="text-xs text-indigo-300 uppercase tracking-wider mb-1">Tokens Secured</p>
                    <p className="text-xl font-bold text-emerald-400">{calculatedTokens.toLocaleString()} LinkKol</p>
                </div>
            </div>
        </div>
      );
  }

  return (
    <div className="bg-gradient-to-br from-indigo-900 to-slate-900 border border-indigo-500/30 rounded-xl overflow-hidden relative group transition-all hover:border-indigo-500/50">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
      
      <div className="p-6 md:p-8 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            
            {/* Left Content */}
            <div className="flex-1 w-full">
                <div className="flex items-center gap-3 mb-4">
                    <span className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                        <Rocket size={12} /> {t.exclusiveAccess}
                    </span>
                </div>
                
                <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
                    {t.kolRoundTitle}
                </h2>
                <p className="text-indigo-200 text-sm mb-6 max-w-lg">
                    Invest in the protocol you use. LinkKol is the first decentralized influence layer.
                </p>

                {/* Round Selector */}
                <div className="mb-6">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-2">{t.selectRound}</p>
                    <div className="flex flex-wrap gap-2">
                        {ROUNDS_CONFIG.map(round => (
                            <button
                                key={round.id}
                                onClick={() => setSelectedRoundId(round.id)}
                                className={`px-4 py-2 rounded-lg text-xs font-bold border transition-all ${
                                    selectedRoundId === round.id 
                                    ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-500/20' 
                                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'
                                }`}
                            >
                                {t.round} {round.id}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-black/20 p-3 rounded-lg border border-white/5">
                        <p className="text-xs text-slate-400 mb-1">{t.tokenPrice}</p>
                        <p className="text-lg font-bold text-white">${currentRound.price.toFixed(3)}</p>
                    </div>
                    <div className="bg-black/20 p-3 rounded-lg border border-white/5">
                        <p className="text-xs text-slate-400 mb-1">{t.valuation}</p>
                        <p className="text-lg font-bold text-white">${(currentRound.fdv / 1_000_000).toFixed(0)}M</p>
                    </div>
                    <div className="bg-black/20 p-3 rounded-lg border border-white/5">
                        <p className="text-xs text-slate-400 mb-1">{t.tokensSold}</p>
                        <p className="text-lg font-bold text-white">{(currentRound.totalTokens / 1_000_000).toFixed(0)}M</p>
                    </div>
                    <div className="bg-black/20 p-3 rounded-lg border border-white/5">
                        <p className="text-xs text-slate-400 mb-1">{t.limitPerKol}</p>
                        <p className="text-lg font-bold text-emerald-400">500K</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="p-1.5 bg-indigo-500/20 rounded-lg mt-0.5">
                            <Lock size={14} className="text-indigo-300" />
                        </div>
                        <div>
                            <p className="text-xs text-indigo-300 font-bold uppercase">{t.vestingSchedule}</p>
                            <p className="text-sm text-slate-300">{t.vestingDetails}</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="p-1.5 bg-orange-500/20 rounded-lg mt-0.5">
                            <Flame size={14} className="text-orange-400" />
                        </div>
                        <div>
                            <p className="text-xs text-orange-400 font-bold uppercase">{t.buybackLogic}</p>
                            <p className="text-sm text-slate-300">{t.buybackDesc}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Action Panel */}
            <div className="w-full md:w-80 bg-slate-900/50 border border-slate-700 rounded-xl p-5 backdrop-blur-sm self-stretch flex flex-col">
                <div className="mb-4">
                    <div className="flex justify-between text-xs text-slate-400 mb-2">
                        <span>{t.raised} ({t.round} {selectedRoundId})</span>
                        <span>{progress.toFixed(1)}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${progress}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                        <span className="text-white font-mono">{(currentRound.raisedTokens / 1_000_000).toFixed(1)}M</span>
                        <span className="text-slate-500 font-mono">{(currentRound.totalTokens / 1_000_000).toFixed(0)}M Cap</span>
                    </div>
                </div>

                <div className="mt-auto">
                    <label className="block text-xs font-bold text-slate-300 uppercase mb-2 flex justify-between">
                        {t.allocation} (USDT)
                        <span className="text-emerald-400">Max: ${maxUsdtAllocation.toLocaleString()}</span>
                    </label>
                    <div className="relative mb-2">
                        <input 
                            type="number" 
                            value={investAmount}
                            onChange={(e) => setInvestAmount(e.target.value)}
                            placeholder="1000"
                            className={`w-full bg-slate-800 border ${isOverLimit ? 'border-rose-500 focus:border-rose-500' : 'border-slate-600 focus:border-indigo-500'} rounded-lg pl-4 pr-12 py-3 text-white focus:outline-none`}
                        />
                        <div className="absolute right-3 top-3 text-slate-500 text-xs font-bold">USDT</div>
                    </div>
                    
                    <div className="flex justify-between text-xs mb-4 px-1">
                        <span className="text-slate-400">You will receive:</span>
                        <span className={`font-bold ${isOverLimit ? 'text-rose-400' : 'text-white'}`}>
                            {calculatedTokens.toLocaleString()} {t.tokens}
                        </span>
                    </div>

                    <button 
                        onClick={handleInvest}
                        disabled={isLoading || !investAmount || isOverLimit || progress >= 100}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-lg shadow-lg shadow-indigo-500/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <span className="animate-pulse">Processing...</span>
                        ) : progress >= 100 ? (
                            <span>Sold Out</span>
                        ) : (
                            <>
                                {t.investNow} <ArrowRight size={16} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
